import * as React from 'react';
import { App } from '../../../../pkg';
import classname from 'classnames';
import {
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalSpaceBetween,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalSpaceBetween,
  ArrowDownToLine,
  ArrowUpToLine,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

import ShapeProperties from './ShapeProperties';
import TextProperties from './TextProperties';
import { IPropertyBarProps } from './type';
import BaseToolBar, { BaseToolBarGroupConfig } from '../BaseToolbar';

import './index.scss';

const renderProperties = (
  selectionType: string,
  app: App,
  version: number,
  onStyleChange?: () => void
) => {
  switch (selectionType) {
    case 'Text':
      return <TextProperties app={app} version={version} onStyleChange={onStyleChange} />;
    case 'Shape':
      return <ShapeProperties app={app} version={version} onStyleChange={onStyleChange} />;
    default:
      return null;
  }
};

interface LayerOrderApi {
  get_selection_count?: () => number;
  can_move_selection_up?: () => boolean;
  can_move_selection_down?: () => boolean;
  move_selection_up?: () => void;
  move_selection_to_top?: () => void;
  move_selection_down?: () => void;
  move_selection_to_bottom?: () => void;
  align_selection_top?: () => void;
  align_selection_bottom?: () => void;
  align_selection_left?: () => void;
  align_selection_right?: () => void;
  distribute_selection_horizontally?: () => void;
  distribute_selection_vertically?: () => void;
}

const PropertyBar = ({ app, version, onStyleChange }: IPropertyBarProps) => {
  const selectionType = app.get_selection_type();
  const content = renderProperties(selectionType, app, version, onStyleChange);
  const appWithLayerOrder = app as unknown as LayerOrderApi;
  const [selectionCount, setSelectionCount] = React.useState(
    () => appWithLayerOrder.get_selection_count?.() ?? 0
  );
  const [canMoveSelectionUp, setCanMoveSelectionUp] = React.useState(
    () => appWithLayerOrder.can_move_selection_up?.() ?? false
  );
  const [canMoveSelectionDown, setCanMoveSelectionDown] = React.useState(
    () => appWithLayerOrder.can_move_selection_down?.() ?? false
  );

  React.useEffect(() => {
    const syncToolbarState = () => {
      const nextSelectionCount = appWithLayerOrder.get_selection_count?.() ?? 0;
      const nextCanMoveUp = appWithLayerOrder.can_move_selection_up?.() ?? false;
      const nextCanMoveDown = appWithLayerOrder.can_move_selection_down?.() ?? false;
      setSelectionCount(prev =>
        prev === nextSelectionCount ? prev : nextSelectionCount
      );
      setCanMoveSelectionUp(prev => (prev === nextCanMoveUp ? prev : nextCanMoveUp));
      setCanMoveSelectionDown(prev => (prev === nextCanMoveDown ? prev : nextCanMoveDown));
    };

    syncToolbarState();
    const timer = window.setInterval(syncToolbarState, 80);
    return () => {
      window.clearInterval(timer);
    };
  }, [appWithLayerOrder, version]);

  const showAlignActions = selectionCount >= 2;
  const showDistributeActions = selectionCount >= 3;
  if (!content && selectionCount <= 0) {
    return null;
  }

  const groups: BaseToolBarGroupConfig[] = [];
  if (showAlignActions) {
    groups.push({
      key: 'align',
      title: 'Align',
      content: (
        <>
          <div className={classname('prop-item', 'prop-align')}>
            <div className="flat-options" role="group" aria-label="Align Selection">
              <button
                type="button"
                className="flat-option flat-option-icon"
                title="顶部对齐"
                aria-label="顶部对齐"
                onClick={() => {
                  appWithLayerOrder.align_selection_top?.();
                }}
              >
                <AlignStartHorizontal />
              </button>
              <button
                type="button"
                className="flat-option flat-option-icon"
                title="底部对齐"
                aria-label="底部对齐"
                onClick={() => {
                  appWithLayerOrder.align_selection_bottom?.();
                }}
              >
                <AlignEndHorizontal />
              </button>
              <button
                type="button"
                className="flat-option flat-option-icon"
                title="左侧对齐"
                aria-label="左侧对齐"
                onClick={() => {
                  appWithLayerOrder.align_selection_left?.();
                }}
              >
                <AlignStartVertical />
              </button>
              <button
                type="button"
                className="flat-option flat-option-icon"
                title="右侧对齐"
                aria-label="右侧对齐"
                onClick={() => {
                  appWithLayerOrder.align_selection_right?.();
                }}
              >
                <AlignEndVertical />
              </button>
            </div>
          </div>
          {showDistributeActions ? (
            <div className={classname('prop-item', 'prop-distribute')}>
              <div className="flat-options" role="group" aria-label="Distribute Selection">
                <button
                  type="button"
                  className="flat-option flat-option-icon"
                  title="横向平均分布"
                  aria-label="横向平均分布"
                  onClick={() => {
                    appWithLayerOrder.distribute_selection_horizontally?.();
                  }}
                >
                  <AlignHorizontalSpaceBetween />
                </button>
                <button
                  type="button"
                  className="flat-option flat-option-icon"
                  title="纵向平均分布"
                  aria-label="纵向平均分布"
                  onClick={() => {
                    appWithLayerOrder.distribute_selection_vertically?.();
                  }}
                >
                  <AlignVerticalSpaceBetween />
                </button>
              </div>
            </div>
          ) : null}
        </>
      ),
    });
  }

  groups.push({
    key: 'layer',
    title: 'Layer',
    content: (
      <div className={classname('prop-item', 'prop-layer-order')}>
        <div className="flat-options" role="group" aria-label="Layer Order">
          <button
            type="button"
            className="flat-option flat-option-icon"
            title="上移一层"
            aria-label="上移一层"
            disabled={!canMoveSelectionUp}
            onClick={() => {
              appWithLayerOrder.move_selection_up?.();
            }}
          >
            <ChevronUp />
          </button>
          <button
            type="button"
            className="flat-option flat-option-icon"
            title="上移到顶部"
            aria-label="上移到顶部"
            disabled={!canMoveSelectionUp}
            onClick={() => {
              appWithLayerOrder.move_selection_to_top?.();
            }}
          >
            <ArrowUpToLine />
          </button>
          <button
            type="button"
            className="flat-option flat-option-icon"
            title="下移一层"
            aria-label="下移一层"
            disabled={!canMoveSelectionDown}
            onClick={() => {
              appWithLayerOrder.move_selection_down?.();
            }}
          >
            <ChevronDown />
          </button>
          <button
            type="button"
            className="flat-option flat-option-icon"
            title="移动到底部"
            aria-label="移动到底部"
            disabled={!canMoveSelectionDown}
            onClick={() => {
              appWithLayerOrder.move_selection_to_bottom?.();
            }}
          >
            <ArrowDownToLine />
          </button>
        </div>
      </div>
    ),
  });

  return (
    <BaseToolBar
      className="propertybar"
      orientation="horizontal"
      ariaLabel="Property toolbar"
      groups={groups}
      groupClassName="prop-items-group"
      titleClassName="prop-section-title"
      dividerClassName="prop-divider"
      showDividers
    >
      {content ? content : null}
    </BaseToolBar>
  );
};

export default PropertyBar;
